import { useEffect } from 'react';

/** Keep the screen on while `active` (lectures, cooking, workouts). Re-acquired when the tab comes back. */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    const nav = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } };
    if (!active || !nav.wakeLock) return;
    let lock: { release(): Promise<void> } | null = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        lock = await nav.wakeLock!.request('screen');
        if (cancelled) void lock.release();
      } catch {
        /* low battery mode etc. – not essential */
      }
    };
    void acquire();
    const onVisible = () => document.visibilityState === 'visible' && !cancelled && void acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release().catch(() => undefined);
    };
  }, [active]);
}

let audio: AudioContext | null = null;

/** Browsers only allow sound after a tap – call this from a click handler before you need to beep. */
export function unlockAudio() {
  try {
    audio ??= new AudioContext();
    if (audio.state === 'suspended') void audio.resume();
  } catch {
    /* no audio */
  }
}

/** A short repeating alarm + vibration (timers). */
export function alarm(times = 4) {
  try {
    navigator.vibrate?.(Array.from({ length: times * 2 - 1 }, (_, i) => (i % 2 ? 120 : 220)));
    audio ??= new AudioContext();
    const t0 = audio.currentTime + 0.05;
    for (let i = 0; i < times; i++) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = i % 2 ? 1046 : 880;
      osc.connect(gain);
      gain.connect(audio.destination);
      const s = t0 + i * 0.4;
      gain.gain.setValueAtTime(0.0001, s);
      gain.gain.exponentialRampToValueAtTime(0.35, s + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, s + 0.3);
      osc.start(s);
      osc.stop(s + 0.32);
    }
  } catch {
    /* no audio */
  }
}

/** localStorage that never throws (private mode, blocked storage). */
export const store = {
  get<T>(key: string, fallback: T): T {
    try {
      const v = localStorage.getItem(key);
      return v == null ? fallback : (JSON.parse(v) as T);
    } catch {
      return fallback;
    }
  },
  set(key: string, value: unknown) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  },
};
